import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import { getImage } from "src/views/shopping/Cart/CartItemFields";
import ItemId from "src/views/shopping/Orders/ItemId";
import useMutation from "src/hooks/useMutation";
import ScanOutModal from "./ScanOutModal";
import ScanOrEnterModal from "./ScanOrEnterModal";
import EditPickLocationsModal from "./EditPickLocationsModal";
import PickStatus from "./PickStatus";
import ReturnToLocationButton from "./ReturnToLocationButton";
import StagedStatusBadge from "./StagedStatusBadge";
import useAuth from "src/hooks/useAuth";
import {
  UPDATE_INVENTORY_REQUEST_MUTATION,
  UPDATE_INTERNAL_WORK_ORDER_MUTATION,
} from "src/constants/graphql/mutations";
import OutboundBillingModal from "./OutboundBillingModal";
import useHasAdditionalCharges from "src/components/capture/ReceivingWizard/hooks/useHasAdditionalCharges";
import useMarkStatusAs from "./hooks/useMarkStatusAs";

export default function ManualLocationItem({
  item,
  asset,
  rackLocations,
  inventoryRequest,
  advancedPickingMode = false,
  showBarcodeColumn = false,
  selected = false,
  onClick,
  onPickKit,
  project = {},
  warehouseLookup = {},
  hasMultipleWarehouses = false,
  orderType = "inventoryRequest",
  showReturnToLocation = false,
  showStagedStatus = false,
}) {
  const { user } = useAuth();
  const { settings } = user.company;
  const [updateInventoryRequest] = useMutation(UPDATE_INVENTORY_REQUEST_MUTATION);
  const [updateInternalWorkOrder] = useMutation(UPDATE_INTERNAL_WORK_ORDER_MUTATION);
  const [openScanModal, setOpenScanModal] = useState(false);
  const [openScanOrEnterModal, setOpenScanOrEnterModal] = useState(false);
  const [openEditLocationsModal, setOpenEditLocationsModal] = useState(false);
  const [openOutboundBillingModal, setOpenOutboundBillingModal] = useState(false);
  const isAdminOrManager = ["admin", "manager", "client"].includes(user.role);
  const hasAdditionalCharges = useHasAdditionalCharges(project, "outbound");
  const { markAs, loading } = useMarkStatusAs(orderType);

  const checkAndUpdateRequestStatus = async () => {
    if (!inventoryRequest) return;

    if (orderType === "internalWorkOrder") {
      const allItemsCompleted = inventoryRequest.internalWorkOrderItems.every(invItem =>
        invItem.id === item.id ? true : invItem.pickedStatus
      );
      if (allItemsCompleted) {
        await updateInternalWorkOrder({
          variables: {
            id: inventoryRequest.id,
            input: {
              vaStatus: {
                picked: true,
              },
            },
          },
        });
      }
    } else {
      const allItemsPicked = inventoryRequest.inventoryItems.every(invItem =>
        invItem.id === item.id ? true : invItem.pickedStatus
      );
      if (allItemsPicked) {
        await updateInventoryRequest({
          variables: {
            id: inventoryRequest.id,
            input: {
              vaStatus: {
                picked: true,
              },
            },
          },
        });
      }

      // COMMENTED OUT: Ancillary Charges modal temporarily disabled for backend work
      // if (hasAdditionalCharges) {
      //   setOpenOutboundBillingModal(true);
      // }
    }
  };

  const handleScanComplete = async barcodeMatch => {
    if (barcodeMatch) {
      // If scan is successful, mark item as picked
      await markAs(item);
      await checkAndUpdateRequestStatus();
    }
  };

  const handleMarkAsPicked = async () => {
    await markAs(item);
    await checkAndUpdateRequestStatus();
  };

  const handlePickKitAction = () => {
    if (onPickKit) {
      onPickKit(asset.id, rackLocations);
    }
  };

  const isKit = asset.type === "Kit";
  const photo = getImage(asset);
  const highlight = item.quantity > asset?.availableQuantity;
  const background = highlight ? "#fffbf2" : undefined;
  const warehouseIds = rackLocations?.map(rl => rl.warehouse?.warehouse_id).filter(Boolean) || [];
  const uniqueWarehouseIds = [...new Set(warehouseIds)];
  const warehouseName = uniqueWarehouseIds.map(id => warehouseLookup[id] || id).join(", ");
  const pickLocations =
    isKit && !item.pickedStatus
      ? ["Various Locations"]
      : rackLocations.map(rl => {
          const locationNames = (rl.warehouse_locations || []).map(loc => loc.location_name).filter(Boolean);
          // Group all warehouse_locations in this rack location entry together with their shared sub_qty
          if (locationNames.length === 0) return null;
          return `${locationNames.join(", ")} (${rl.sub_qty})`;
        }).filter(Boolean);

  const handleScanOut = () => {
    if (isAdminOrManager) {
      setOpenScanOrEnterModal(true);
    } else {
      setOpenScanModal(true);
    }
  };

  const handleEditLocations = () => {
    setOpenEditLocationsModal(true);
  };

  return (
    <>
      <TableRow hover selected={selected} onClick={onClick}>
        <TableCell align="center" sx={{ p: 0 }}>
          <Box sx={{ background, p: 1, m: 1, width: "100%", height: "100%" }}>
            {photo ? (
              <img style={{ maxWidth: 90, maxHeight: 60 }} src={photo} alt="" />
            ) : (
              "No image"
            )}
          </Box>
        </TableCell>
        <TableCell align="center">
          <ItemId asset={asset} />
        </TableCell>
        {showBarcodeColumn && <TableCell align="center">{asset.barcode || ""}</TableCell>}
        <TableCell align="center">{asset.description}</TableCell>
        <TableCell align="center">{item.quantity}</TableCell>
        <TableCell align="center">
          <Box display="flex" alignItems="center" justifyContent="center">
            <Box>
              {pickLocations.map((value, index) => (
                <Box key={index} textAlign="center">
                  {value}
                </Box>
              ))}
            </Box>
            {asset.rackLocations?.length > 1 && !item.pickedStatus && (
              <Tooltip title="Edit Pick Locations">
                <IconButton size="small" sx={{ ml: 0.5 }} onClick={handleEditLocations}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </TableCell>
        {hasMultipleWarehouses && <TableCell align="center">{warehouseName || "N/A"}</TableCell>}
        {showStagedStatus && (
          <TableCell align="center">
            <StagedStatusBadge stagedStatus={item.stagedStatus} />
          </TableCell>
        )}
        <TableCell align="center">
          <PickStatus
            pickedStatus={item.pickedStatus}
            advancedPickingMode={advancedPickingMode}
            isKit={isKit}
            loading={loading}
            scanOutModal={settings.scan_out_modal}
            orderType={orderType}
            onPickKit={handlePickKitAction}
            onScanOut={handleScanOut}
            onMarkAsPicked={handleMarkAsPicked}
          />
        </TableCell>
        {orderType === "internalWorkOrder" && showReturnToLocation && (
          <TableCell align="center">
            <ReturnToLocationButton item={item} asset={asset} pickLocations={pickLocations} />
          </TableCell>
        )}
      </TableRow>

      {openScanModal && (
        <ScanOutModal
          asset={asset}
          itemId={asset?.itemId}
          onClose={() => setOpenScanModal(false)}
          onScanComplete={handleScanComplete}
        />
      )}

      {openScanOrEnterModal && (
        <ScanOrEnterModal
          asset={asset}
          onClose={() => setOpenScanOrEnterModal(false)}
          openScanModal={() => setOpenScanModal(true)}
          markAsPicked={handleMarkAsPicked}
        />
      )}

      {openEditLocationsModal && (
        <EditPickLocationsModal
          asset={asset}
          rackLocations={rackLocations}
          item={item}
          inventoryRequest={inventoryRequest}
          onClose={() => setOpenEditLocationsModal(false)}
        />
      )}
      <OutboundBillingModal
        open={openOutboundBillingModal}
        inventoryRequest={inventoryRequest}
        project={project}
        onClose={() => setOpenOutboundBillingModal(false)}
      />
    </>
  );
}
